import {
  type FieldCatalogue,
  type FindingSeverity,
  type FindingView,
  isRequesterFinding,
  type RequestPanel as Panel,
  type RequestFieldView,
} from "@procuremate/shared";
import { clsx } from "clsx";
import {
  ChevronDown,
  CircleCheck,
  Hand,
  Info,
  Maximize2,
  Minimize2,
  OctagonAlert,
  Pencil,
  TriangleAlert,
} from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Language } from "@/i18n";
import { BeforeYouSend } from "./before-you-send";
import { ClassificationEditor } from "./classification-editor";
import { DocumentList } from "./document-list";
import { displayValue, withLabels } from "./field-format";
import { FieldRow } from "./field-row";
import { RequestStatusBadge } from "./request-status-badge";
import { SendBar } from "./send-bar";

export { FieldRow } from "./field-row";

const SEVERITY_ICONS: Record<FindingSeverity, typeof Info> = {
  INFO: Info,
  WARN: TriangleAlert,
  HIGH: OctagonAlert,
};

/** The facts a buyer looks at first (WP-2.8): shown on top when recorded. */
const KEY_FACTS = [
  ["supplier_mentioned", "supplier"],
  ["supplier_quoted_price", "total"],
  ["budget_available", "budget"],
  ["target_go_live", "date"],
] as const;

/** How long a field changed by the latest turn stays highlighted. */
const HIGHLIGHT_MS = 8000;

/**
 * Findings the requester sees (WP-2.8, ADR-0030): open points of the types they can act on. The
 * server sends no others to the requester; the filter keeps pages that also show buyer data honest.
 */
export function requesterFindings(findings: FindingView[]): FindingView[] {
  return findings.filter((f) => f.resolvedAt === null && isRequesterFinding(f.type));
}

/**
 * The live request panel (ADR-0014, redesigned in WP-2.8): header with type and progress, key
 * facts, "Before you send", the form by registry section (collapsible; missing items inside their
 * section), documents, and a fixed bar with the save state and Send. Updated from `case.updated`,
 * edits and periodic refreshes — never by a reload.
 */
export function RequestPanel({
  panel,
  catalogue,
  canEdit,
  editBlocked,
  suggestSubmit = false,
  expanded = false,
  onToggleExpand,
}: {
  panel: Panel;
  catalogue: FieldCatalogue | undefined;
  /** The viewer is the requester and the request is open. */
  canEdit: boolean;
  /** A reply is being written: edits wait (the API would answer 409). */
  editBlocked: boolean;
  /** The last turn's model hint: highlight Send (it never sends). */
  suggestSubmit?: boolean;
  /** Wide screens: the panel fills the page for a final review (WP-2.8). */
  expanded?: boolean;
  onToggleExpand?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language as Language;
  const titleId = useId();
  const entries = new Map(catalogue?.fields.map((f) => [f.key, f]));
  const fields = new Map(panel.fields.map((f) => [f.key, f]));
  const { gate } = panel;
  const classified = panel.engagementShape !== null;
  const total = gate.applicableMandatory.length;
  const done = total - gate.openMandatory.length;
  const [classifying, setClassifying] = useState(false);
  const changeButton = useRef<HTMLButtonElement>(null);
  // Focus returns to "Change" once the editor has closed and the button is mounted again — in an
  // effect, after the commit (a requestAnimationFrame could run before it and find no button).
  const returnFocus = useRef(false);
  useEffect(() => {
    if (classifying || !returnFocus.current) return;
    returnFocus.current = false;
    changeButton.current?.focus();
  }, [classifying]);

  // A saved element can move (from "Add" to a value): once the refreshed panel shows it, focus goes to
  // its button again.
  const [focusAfterSave, setFocusAfterSave] = useState<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-check on every panel refresh.
  useEffect(() => {
    if (!focusAfterSave) return;
    const button = document.querySelector<HTMLButtonElement>(
      `button[data-field-button="${focusAfterSave}"][data-has-value="true"]`,
    );
    if (button) {
      button.focus();
      setFocusAfterSave(null);
    }
  }, [focusAfterSave, panel]);

  // Fields the latest turn changed (WP-2.8): highlighted for a while and announced. Only changes that
  // arrive while a reply is being written count — not the requester's own edits. A turn's last events
  // can land in the same render that ends the reply, so the render right after one counts too.
  const seen = useRef<Map<string, string> | null>(null);
  const replyRunning = useRef(editBlocked);
  const [highlighted, setHighlighted] = useState<string[]>([]);
  const [announced, setAnnounced] = useState("");
  useEffect(() => {
    const now = new Map(panel.fields.map((f) => [f.key, f.updatedAt]));
    const before = seen.current;
    const fromTurn = editBlocked || replyRunning.current;
    seen.current = now;
    replyRunning.current = editBlocked;
    if (!before || !fromTurn) return;
    const changed = [...now].filter(([k, at]) => before.get(k) !== at).map(([k]) => k);
    if (changed.length === 0) return;
    setHighlighted(changed);
    const labelOf = (k: string) => catalogue?.fields.find((f) => f.key === k)?.labels[language] ?? k;
    setAnnounced(t("requests.panel.updatedAnnouncement", { fields: changed.map(labelOf).join(", ") }));
  }, [panel.fields, editBlocked, t, language, catalogue]);
  useEffect(() => {
    if (highlighted.length === 0) return;
    const timer = setTimeout(() => setHighlighted([]), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [highlighted]);

  // "Fix it": open the section holding the field and its editor.
  const [fixing, setFixing] = useState<{ key: string; n: number } | null>(null);

  const required = new Set(classified ? gate.openMandatory.filter((k) => !gate.skippable.includes(k)) : []);
  const skippable = new Set(classified ? gate.skippable : []);
  const row = (key: string, field: RequestFieldView | undefined) => (
    <FieldRow
      key={key}
      requestId={panel.id}
      fieldKey={key}
      entry={entries.get(key)}
      field={field}
      canEdit={canEdit}
      editBlocked={editBlocked}
      required={required.has(key)}
      skippable={skippable.has(key)}
      highlighted={highlighted.includes(key)}
      openEditor={fixing?.key === key ? fixing.n : 0}
      documents={panel.documents}
      onSaved={setFocusAfterSave}
    />
  );

  return (
    // Wide screens (side by side): the panel's content scrolls in its own area and the bar sits below it,
    // never over a field. Expanded, or on phones, the page scrolls and the bar stays at the bottom.
    <section
      aria-labelledby={titleId}
      className={clsx("@container flex flex-col gap-3", !expanded && "lg:max-h-full lg:min-h-0")}
    >
      <div className={clsx("flex flex-col gap-3", !expanded && "lg:min-h-0 lg:overflow-y-auto lg:px-1 lg:pb-1")}>
        <div className="rounded-lg bg-surface p-4 shadow-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 id={titleId} className="text-lg font-bold text-primary">
              {t("requests.panel.title")}
            </h2>
            <div className="flex items-center gap-2">
              <RequestStatusBadge status={panel.status} />
              {onToggleExpand ? (
                <button
                  type="button"
                  aria-pressed={expanded}
                  aria-label={expanded ? t("requests.panel.bar.collapse") : t("requests.panel.bar.expandLabel")}
                  className="hidden h-8 items-center gap-1 rounded-sm px-2 text-sm font-semibold text-link hover:bg-bg lg:inline-flex"
                  onClick={onToggleExpand}
                >
                  {expanded ? (
                    <Minimize2 aria-hidden="true" className="h-4 w-4" />
                  ) : (
                    <Maximize2 aria-hidden="true" className="h-4 w-4" />
                  )}
                  {expanded ? t("requests.panel.bar.collapse") : t("requests.panel.bar.expand")}
                </button>
              ) : null}
            </div>
          </div>
          <p className="mt-1 text-sm text-fg-muted">
            <span className="tabular">{panel.reference}</span>
            {panel.title ? ` · ${panel.title}` : null}
          </p>
          <div className="mt-2 flex items-start justify-between gap-2 text-sm">
            <p>
              <span className="font-medium text-fg">{t("requests.panel.classification")}: </span>
              {panel.engagementShape ? (
                <span>
                  {t(`requests.panel.shapes.${panel.engagementShape}`)}
                  {panel.sizeTier ? ` · ${t(`requests.panel.tiers.${panel.sizeTier}`)}` : null}
                  {panel.classificationSource === "REQUESTER" ? (
                    <span className="text-fg-muted"> ({t("requests.classification.setByYou")})</span>
                  ) : null}
                </span>
              ) : (
                <span className="text-fg-muted">{t("requests.panel.notClassified")}</span>
              )}
            </p>
            {canEdit && !classifying ? (
              <button
                ref={changeButton}
                type="button"
                disabled={editBlocked}
                className="inline-flex shrink-0 items-center gap-1 rounded-sm px-2 py-1 text-sm font-semibold text-link hover:bg-bg disabled:cursor-not-allowed disabled:opacity-60"
                aria-label={t("requests.classification.change")}
                onClick={() => setClassifying(true)}
              >
                <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
                {t("requests.classification.changeShort")}
              </button>
            ) : null}
          </div>
          {classifying ? (
            <ClassificationEditor
              panel={panel}
              onClose={() => {
                returnFocus.current = true;
                setClassifying(false);
              }}
            />
          ) : null}
          {total > 0 ? (
            <div className="mt-3">
              <p className="text-sm text-fg">{t("requests.panel.progress", { done, total })}</p>
              <div
                role="progressbar"
                aria-label={t("requests.panel.progressLabel")}
                aria-valuemin={0}
                aria-valuemax={total}
                aria-valuenow={done}
                aria-valuetext={t("requests.panel.progress", { done, total })}
                className="mt-1.5 h-2 overflow-hidden rounded-full bg-bg"
              >
                <div
                  className="h-full rounded-full bg-primary transition-[width] duration-200"
                  style={{ width: `${Math.round((done / total) * 100)}%` }}
                />
              </div>
            </div>
          ) : null}
          {!classified && canEdit ? (
            <p className="mt-2 text-sm text-fg-muted">{t("requests.panel.provisionalNeeded")}</p>
          ) : null}
          {gate.canSubmit && canEdit ? (
            <p className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-success-bg px-2 py-1 text-sm font-semibold text-success">
              <CircleCheck aria-hidden="true" className="h-4 w-4" />
              {t("requests.panel.ready")}
            </p>
          ) : null}
          {panel.submittedAt ? (
            <p className="mt-3 text-sm text-fg">
              {t("requests.submit.submittedOn", {
                date: new Intl.DateTimeFormat(language, { dateStyle: "medium", timeStyle: "short" }).format(
                  new Date(panel.submittedAt),
                ),
              })}
            </p>
          ) : null}
        </div>

        <KeyFacts fields={fields} catalogue={catalogue} />

        <BeforeYouSend
          requestId={panel.id}
          findings={panel.findings}
          catalogue={catalogue}
          canAct={canEdit}
          busy={editBlocked}
          onFixField={(key) => setFixing((f) => ({ key, n: (f?.n ?? 0) + 1 }))}
          missing={
            classified && canEdit
              ? gate.openMandatory.map((key) => ({ key, label: entries.get(key)?.labels[language] ?? key }))
              : []
          }
        />

        <FormSections
          panel={panel}
          catalogue={catalogue}
          classified={classified}
          canEdit={canEdit}
          forceOpen={fixing?.key ?? null}
          row={row}
        />

        {panel.documents.length > 0 ? (
          <Collapsible
            title={t("requests.documents.title")}
            state={<span className="tabular">{panel.documents.length}</span>}
            defaultOpen={false}
          >
            <DocumentList
              requestId={panel.id}
              documents={panel.documents}
              canRemove={canEdit}
              removeBlocked={editBlocked}
            />
          </Collapsible>
        ) : null}

        <p aria-live="polite" className="sr-only">
          {announced}
        </p>
      </div>
      {canEdit ? (
        <SendBar
          panel={panel}
          catalogue={catalogue}
          saving={editBlocked}
          suggestSubmit={suggestSubmit}
          belowContent={!expanded}
        />
      ) : null}
    </section>
  );
}

/** Supplier, offer total, budget and target date when recorded (WP-2.8). */
function KeyFacts({
  fields,
  catalogue,
}: {
  fields: Map<string, RequestFieldView>;
  catalogue: FieldCatalogue | undefined;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language as Language;
  const titleId = useId();
  const facts = KEY_FACTS.map(([key, name]) => {
    const field = fields.get(key);
    const entry = catalogue?.fields.find((f) => f.key === key);
    const value = field?.status === "CAPTURED" ? displayValue(field, entry, language, t) : null;
    return { key, name, value: Array.isArray(value) ? value.join(", ") : value };
  });
  if (facts.every((f) => !f.value)) return null;
  return (
    <section aria-labelledby={titleId} className="rounded-lg bg-surface px-4 py-3 shadow-card">
      <h3 id={titleId} className="sr-only">
        {t("requests.panel.keyFacts.title")}
      </h3>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 @min-[30rem]:grid-cols-4">
        {facts.map((f) => (
          <div key={f.key} className="min-w-0">
            <dt className="text-xs font-semibold tracking-wide text-fg-muted uppercase">
              {t(`requests.panel.keyFacts.${f.name}`)}
            </dt>
            <dd className={clsx("text-sm break-words", f.value ? "font-semibold text-fg" : "text-fg-muted")}>
              {f.value || t("requests.panel.keyFacts.none")}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * The form by registry section (WP-2.8): recorded values and the missing mandatory items inside
 * their section; optional items that apply listed apart. A section is open while something required
 * is missing and closed when complete, unless the requester toggled it.
 */
function FormSections({
  panel,
  catalogue,
  classified,
  canEdit,
  forceOpen,
  row,
}: {
  panel: Panel;
  catalogue: FieldCatalogue | undefined;
  classified: boolean;
  canEdit: boolean;
  /** A field "Fix it" is opening: its section opens. */
  forceOpen: string | null;
  row: (key: string, field: RequestFieldView | undefined) => ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language as Language;
  const { gate } = panel;
  const fields = new Map(panel.fields.map((f) => [f.key, f]));
  const sectionOf = new Map(catalogue?.fields.map((f) => [f.key, f.section]));
  const order = new Map(catalogue?.fields.map((f, i) => [f.key, i]));
  const byOrder = (a: string, b: string) => (order.get(a) ?? 0) - (order.get(b) ?? 0);
  // Before the type is known the mandatory set is provisional (ADR-0016): listed, but not counted as
  // missing and its sections start closed.
  const missing = gate.openMandatory;
  const recorded = panel.fields.filter((f) => !missing.includes(f.key)).map((f) => f.key);
  const optional = canEdit ? gate.applicableOptional.filter((k) => !fields.has(k)) : [];

  if (!catalogue) return null;
  const sections = catalogue.sections
    .map((section) => {
      const inSection = (k: string) => sectionOf.get(k) === section.key;
      return {
        section,
        missing: missing.filter(inSection),
        shown: [...new Set([...missing.filter(inSection), ...recorded.filter(inSection)])].sort(byOrder),
        optional: optional.filter(inSection),
      };
    })
    .filter((s) => s.shown.length > 0 || s.optional.length > 0);
  if (sections.length === 0) {
    return (
      <p className="rounded-lg bg-surface p-4 text-sm text-fg-muted shadow-card">{t("requests.panel.noDetails")}</p>
    );
  }
  return (
    <>
      {sections.map((s) => (
        <Collapsible
          key={s.section.key}
          title={s.section.labels[language]}
          defaultOpen={classified && s.missing.length > 0}
          forceOpen={forceOpen !== null && sectionOf.get(forceOpen) === s.section.key}
          state={
            <span className="flex flex-wrap items-center justify-end gap-x-2 text-xs">
              {classified && s.missing.length > 0 ? (
                <span className="font-semibold text-warning-fg">
                  {t("requests.panel.section.missing", { count: s.missing.length })}
                </span>
              ) : s.shown.length > 0 && s.missing.length === 0 ? (
                <span className="inline-flex items-center gap-1 text-success">
                  <CircleCheck aria-hidden="true" className="h-3.5 w-3.5" />
                  {t("requests.panel.section.complete")}
                </span>
              ) : null}
              {s.optional.length > 0 ? (
                <span className="text-fg-muted">
                  {t("requests.panel.section.optional", { count: s.optional.length })}
                </span>
              ) : null}
            </span>
          }
        >
          {s.shown.length > 0 ? (
            <div className="divide-y divide-border">{s.shown.map((k) => row(k, fields.get(k)))}</div>
          ) : (
            <p className="text-sm text-fg-muted">{t("requests.panel.section.empty")}</p>
          )}
          {s.optional.length > 0 ? (
            // Opened when "Fix it" targets one of its items.
            <details
              className="mt-2 border-t border-border pt-2"
              open={forceOpen !== null && s.optional.includes(forceOpen) ? true : undefined}
            >
              <summary className="cursor-pointer text-sm font-semibold text-link">
                {t("requests.panel.section.showOptional", { count: s.optional.length })}
              </summary>
              <div className="divide-y divide-border">{s.optional.map((k) => row(k, undefined))}</div>
            </details>
          ) : null}
        </Collapsible>
      ))}
    </>
  );
}

/** A panel card whose body opens and closes; the header carries its state (WP-2.8). */
function Collapsible({
  title,
  state,
  defaultOpen,
  forceOpen = false,
  children,
}: {
  title: string;
  state?: ReactNode;
  defaultOpen: boolean;
  forceOpen?: boolean;
  children: ReactNode;
}) {
  const bodyId = useId();
  const headingId = useId();
  // null: follow the default (it changes as items are answered); set once the requester toggles.
  const [chosen, setChosen] = useState<boolean | null>(null);
  useEffect(() => {
    if (forceOpen) setChosen(true);
  }, [forceOpen]);
  const open = chosen ?? defaultOpen;
  return (
    <section aria-labelledby={headingId} className="rounded-lg bg-surface shadow-card">
      <h3 id={headingId} className="font-heading text-base font-semibold text-fg">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          className="flex w-full items-center justify-between gap-3 rounded-lg px-4 py-3 text-left hover:bg-bg"
          onClick={() => setChosen(!open)}
        >
          <span className="flex min-w-0 items-center gap-2">
            <ChevronDown
              aria-hidden="true"
              className={clsx("h-4 w-4 shrink-0 text-fg-muted transition-transform", !open && "-rotate-90")}
            />
            <span className="break-words">{title}</span>
          </span>
          {state}
        </button>
      </h3>
      <div id={bodyId} hidden={!open} className="px-4 pb-3">
        {children}
      </div>
    </section>
  );
}

/**
 * Points for the buyer, keys shown as labels. The requester sees only the open points they can act
 * on; the buyer's review page (WP-3.1) shows everything — closed points marked answered (WP-2.7),
 * points the requester left to procurement marked so (WP-2.8).
 */
export function FindingList({
  findings,
  catalogue,
  audience = "requester",
}: {
  findings: FindingView[];
  catalogue: FieldCatalogue | undefined;
  audience?: "requester" | "buyer";
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language as Language;
  return (
    <ul className="flex flex-col gap-2">
      {(audience === "buyer" ? findings : requesterFindings(findings)).map((f) => {
        const Icon = f.resolvedAt ? CircleCheck : SEVERITY_ICONS[f.severity];
        return (
          <li
            key={f.id}
            className={`rounded-md border border-border border-l-4 p-3 ${f.resolvedAt ? "border-l-border" : "border-l-finding"}`}
          >
            <p
              className={`flex items-center gap-1.5 text-sm font-semibold ${f.resolvedAt ? "text-fg-muted" : "text-finding"}`}
            >
              <Icon aria-hidden="true" className="h-4 w-4" />
              {f.resolvedAt ? t("requests.panel.answered") : t(`requests.panel.severity.${f.severity}`)}
            </p>
            <p className={`mt-1 text-sm whitespace-pre-wrap break-words ${f.resolvedAt ? "text-fg-muted" : "text-fg"}`}>
              {withLabels(f.message, catalogue, language)}
            </p>
            {f.resolution ? (
              <p className="mt-1 text-sm whitespace-pre-wrap break-words text-fg">
                <span className="font-semibold">{t("requests.panel.resolution")}</span>{" "}
                {withLabels(f.resolution, catalogue, language)}
              </p>
            ) : null}
            {f.leftToBuyerAt && audience === "buyer" ? (
              <p className="mt-1 flex items-center gap-1 text-sm font-semibold text-fg">
                <Hand aria-hidden="true" className="h-3.5 w-3.5" />
                {t("review.leftToBuyer")}
              </p>
            ) : null}
            <p className="mt-1 text-xs text-fg-muted">{t(`requests.panel.origin.${f.origin}`)}</p>
          </li>
        );
      })}
    </ul>
  );
}

/** Recorded values grouped by registry section, in registry order (submit page, buyer's review). */
export function DetailsBySection({
  fields,
  catalogue,
  row,
}: {
  fields: RequestFieldView[];
  catalogue: FieldCatalogue | undefined;
  row: (key: string, field: RequestFieldView) => ReactNode;
}) {
  const { i18n } = useTranslation();
  const language = i18n.language as Language;
  const sectionOf = new Map(catalogue?.fields.map((f) => [f.key, f.section]));
  return (
    <div className="@container">
      {(catalogue?.sections ?? []).map((section) => {
        const inSection = fields.filter((f) => sectionOf.get(f.key) === section.key);
        if (inSection.length === 0) return null;
        return (
          <div key={section.key} className="mt-3 first:mt-0">
            <h4 className="text-xs font-semibold tracking-wide text-fg-muted uppercase">{section.labels[language]}</h4>
            <div className="divide-y divide-border">{inSection.map((f) => row(f.key, f))}</div>
          </div>
        );
      })}
    </div>
  );
}
